import { Box, Paragraph, Table, Tag, Text } from '@zillow/constellation-10';
import type { CustomerAnalyticsEnterpriseDailyAnalyticsDto } from '@zg-rentals/ts-customer-analytics-client';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { formatNumber } from '../../../../utils';
import { TableType } from './AdSpendReport.config';

type PropertyDetailTableProps = {
  dailyBreakdowns: Array<CustomerAnalyticsEnterpriseDailyAnalyticsDto>;
  tableType?: TableType;
};

const formatStat = (value: number | undefined): string | number =>
  typeof value === 'number' && value > -1 ? formatNumber(value) : '-';

export const PropertyDetailTable = ({ dailyBreakdowns, tableType }: PropertyDetailTableProps) => {
  const isExtend = tableType === TableType.EXTEND;
  const isBoost = tableType === TableType.BOOST;

  const sortedData = [...dailyBreakdowns]
    .filter((breakdown) => {
      if (isExtend) {
        return (breakdown.addOnImpressions ?? 0) > 0 || (breakdown.addOnClickCount ?? 0) > 0;
      }
      if (isBoost) {
        return (breakdown.regularBoostImpressions ?? 0) > 0 || (breakdown.regularBoostClicks ?? 0) > 0;
      }
      const hasData =
        (breakdown.impressionCount ?? 0) > 0 ||
        (breakdown.viewCount ?? 0) > 0 ||
        (breakdown.leadCount ?? 0) > 0 ||
        (breakdown.listingWebReferrals ?? 0) > 0 ||
        (breakdown.adClicks ?? 0) > 0;
      return hasData;
    })
    .sort((a, b) => {
      const dateA = localDateToDate(a.reportDate);
      const dateB = localDateToDate(b.reportDate);
      return dateB.getTime() - dateA.getTime();
    });

  const formatDate = (reportDate: CustomerAnalyticsEnterpriseDailyAnalyticsDto['reportDate']) => {
    const date = localDateToDate(reportDate);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  if (sortedData.length === 0) {
    return (
      <Box css={{ padding: '800', textAlign: 'center' }}>
        <Paragraph>No data available for the selected period.</Paragraph>
      </Box>
    );
  }

  return (
    <Box css={{ marginTop: '600' }}>
      <Text css={{ fontWeight: 'bold', marginBottom: '400', display: 'block' }}>Performance by date</Text>
      <Table.Responsive>
        <Table.Root aria-label="Property daily breakdown table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              {isExtend ? (
                <>
                  <Table.HeaderCell>Total Impressions</Table.HeaderCell>
                  <Table.HeaderCell>Total Clicks</Table.HeaderCell>
                  <Table.HeaderCell>Meta Impressions</Table.HeaderCell>
                  <Table.HeaderCell>Meta Clicks</Table.HeaderCell>
                  <Table.HeaderCell>TikTok Impressions</Table.HeaderCell>
                  <Table.HeaderCell>TikTok Clicks</Table.HeaderCell>
                </>
              ) : isBoost ? (
                <>
                  <Table.HeaderCell>Impressions</Table.HeaderCell>
                  <Table.HeaderCell>Ad Clicks</Table.HeaderCell>
                </>
              ) : (
                <>
                  <Table.HeaderCell>Impressions</Table.HeaderCell>
                  <Table.HeaderCell>Listing Views</Table.HeaderCell>
                  <Table.HeaderCell>Leads</Table.HeaderCell>
                  <Table.HeaderCell>Listing Web Referrals</Table.HeaderCell>
                  <Table.HeaderCell>Ad Clicks</Table.HeaderCell>
                </>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.map((breakdown, index) => (
              <Table.Row
                key={`${breakdown.reportDate.year}-${breakdown.reportDate.month}-${breakdown.reportDate.day}-${index}`}
              >
                <Table.Cell>
                  <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
                    <Text>{formatDate(breakdown.reportDate)}</Text>
                    {breakdown.totalDaysSpotlighted > 0 && (
                      <Tag size="sm" tone="blue">
                        Spotlight
                      </Tag>
                    )}
                  </Box>
                </Table.Cell>
                {isExtend ? (
                  <>
                    <Table.Cell>{formatStat(breakdown.addOnImpressions)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.addOnClickCount)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.addOnMetaImpressions)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.addOnMetaClicks)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.addOnTiktokImpressions)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.addOnTiktokClicks)}</Table.Cell>
                  </>
                ) : isBoost ? (
                  <>
                    <Table.Cell>{formatStat(breakdown.regularBoostImpressions)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.regularBoostClicks)}</Table.Cell>
                  </>
                ) : (
                  <>
                    <Table.Cell>{formatStat(breakdown.impressionCount)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.viewCount)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.leadCount)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.listingWebReferrals)}</Table.Cell>
                    <Table.Cell>{formatStat(breakdown.adClicks)}</Table.Cell>
                  </>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
    </Box>
  );
};
